import { AnalyticsBrowser } from "@customerio/cdp-analytics-browser";
import authHelper from "@/helpers/authHelper";

/**
 * Customer.io Data Pipelines (CDP) browser source.
 *
 * Loads the Customer.io analytics client and enables in-app messaging so that
 * inline campaigns can render into the anchor elements defined in
 * `@/types/customerio` (see `CustomerioBanner.vue`). Mirrors the way
 * `datadog.ts` wraps the RUM SDK: a thin, side-effect-light module that the app
 * initializes once at start-up.
 *
 * The write key is runtime-configurable (`window.__APP_CONFIG__`) so a single
 * image can point at the correct Customer.io source per environment. When the
 * key is unset (e.g. a dev environment without Customer.io) initialization is
 * skipped and the helpers become no-ops.
 */
const writeKey = process.env.VUE_APP_CUSTOMERIO_WRITE_KEY as string;

let cioanalytics: AnalyticsBrowser | undefined;

/**
 * Load the Customer.io CDP source and enable in-app messaging.
 *
 * Safe to call multiple times: the client is created only once. Returns the
 * client instance, or `undefined` when no write key is configured.
 */
export const initCustomerio = (): AnalyticsBrowser | undefined => {
  if (cioanalytics) {
    return cioanalytics;
  }
  if (!writeKey) {
    console.warn("Customer.io write key not set; skipping CDP source initialization.");
    return undefined;
  }

  cioanalytics = AnalyticsBrowser.load(
    { writeKey },
    {
      integrations: {
        // The In-App plugin renders the inline/message creatives. `colorScheme`
        // follows the OS `prefers-color-scheme`; `anonymousInApp` lets visitors
        // receive campaigns before they are identified.
        "Customer.io In-App Plugin": {
          colorScheme: "auto",
          anonymousInApp: true,
        },
      },
    }
  );

  return cioanalytics;
};

/**
 * Associate the current visitor with a Customer.io profile using the device tag
 * as the user identifier.
 *
 * SECURITY: the device tag travels in the public route URL (`/device/:deviceTag`)
 * and is only an identifier, not proof of ownership. We therefore identify *only*
 * after the backend has authenticated this browser for that exact device tag —
 * never straight from the URL — so opening someone else's device link cannot
 * subscribe this browser to their in-app messages. No-op until `initCustomerio`
 * has run.
 */
export const identifyDevice = (deviceTag: string | undefined): void => {
  if (!deviceTag) {
    return;
  }
  // Only identify a device tag the backend has authenticated this browser for.
  if (authHelper.getAuthenticatedDeviceTag() !== deviceTag) {
    return;
  }
  void cioanalytics?.identify(deviceTag);
};

/**
 * Clear the Customer.io identity so a profile never outlives its session — e.g.
 * on a shared store device the previous customer's identity must not carry into
 * the next visitor. Called from `authHelper.logout()`.
 */
export const resetCustomerio = (): void => {
  void cioanalytics?.reset();
};

/**
 * Record a page view. Required for single-page apps so Customer.io can evaluate
 * page rules and deliver in-app messages on each route change.
 */
export const trackPage = (): void => {
  void cioanalytics?.page();
};
